import React, { useEffect, useState } from 'react';
import { auth } from '../firebase';
import { FaClock, FaHeartbeat, FaBolt, FaChartLine, FaFileDownload } from 'react-icons/fa';
import { loadUserTrainingZones } from '../utils/planGenerator';
import { buildTcxWorkout, buildFitWorkout, downloadWorkoutFile } from '../utils/workoutExport';
import './WorkoutDetailView.css';

function toDate(value) {
  if (!value) return null;
  if (typeof value?.toDate === 'function') {
    try {
      return value.toDate();
    } catch {
      return null;
    }
  }
  if (value instanceof Date) return value;
  if (typeof value === 'number') return new Date(value);
  if (typeof value === 'string') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

function formatDuration(seconds) {
  if (!seconds) return '0 min';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) {
    return `${hours}h${minutes}min`;
  }
  return `${minutes}min`;
}

function formatTime(seconds) {
  if (!seconds) return '0:00';
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${minutes}:${String(secs).padStart(2, '0')}`;
}

function getZoneColor(zone) {
  const colors = {
    1: '#26A69A', // Turquoise - Recovery
    2: '#4DB6AC', // Light Turquoise - Aerobic
    3: '#FFC107', // Yellow - Tempo
    4: '#8b7fd4', // Threshold — brand purple
    5: '#6a5cb8', // VO2max — brand purple
  };
  return colors[zone] || '#9E9E9E';
}

function getZoneName(zone) {
  const names = {
    1: 'Recovery',
    2: 'Aerobic',
    3: 'Tempo',
    4: 'Threshold',
    5: 'VO2max',
  };
  return names[zone] || `Zone ${zone}`;
}

export default function WorkoutDetailInline({ workout }) {
  const user = auth.currentUser;
  const [intensityProfile, setIntensityProfile] = useState([]);
  const [trainingZones, setTrainingZones] = useState(null);
  const [hoveredSegment, setHoveredSegment] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [exportingFit, setExportingFit] = useState(false);
  const [exportingTcx, setExportingTcx] = useState(false);

  useEffect(() => {
    if (workout) {
      generateIntensityProfile(workout);
      loadTrainingZones(workout.sport || 'cycling');
    }
  }, [workout]);

  async function loadTrainingZones(sport) {
    if (!user) return;
    try {
      const { powerZones, hrZones } = await loadUserTrainingZones(user.uid, sport || 'cycling');
      setTrainingZones({ powerZones: powerZones || null, hrZones: hrZones || null });
    } catch (error) {
      console.error('Error loading training zones:', error);
    }
  }

  function generateIntensityProfile(workoutData) {
    const profile = [];
    const structure = workoutData.structure || { type: 'simple' };
    let currentTime = 0;

    if (structure.type === 'intervals' && structure.intervals) {
      const { warmup, sets, cooldown } = structure.intervals;

      if (warmup) {
        profile.push({
          start: currentTime,
          end: currentTime + warmup.duration,
          zone: warmup.intensity,
          type: 'warmup',
          label: 'Warmup',
        });
        currentTime += warmup.duration;
      }

      sets.forEach((set, setIndex) => {
        for (let rep = 0; rep < set.repetitions; rep++) {
          profile.push({
            start: currentTime,
            end: currentTime + set.workDuration,
            zone: set.workIntensity,
            type: 'work',
            label: `Interval ${rep + 1}`,
          });
          currentTime += set.workDuration;

          if (rep < set.repetitions - 1) {
            profile.push({
              start: currentTime,
              end: currentTime + set.restDuration,
              zone: set.restIntensity,
              type: 'rest',
              label: 'Rest',
            });
            currentTime += set.restDuration;
          }
        }

        if (set.sets && setIndex < sets.length - 1 && set.setRestDuration) {
          profile.push({
            start: currentTime,
            end: currentTime + set.setRestDuration,
            zone: set.restIntensity,
            type: 'rest',
            label: 'Set Rest',
          });
          currentTime += set.setRestDuration;
        }
      });

      if (cooldown) {
        profile.push({
          start: currentTime,
          end: currentTime + cooldown.duration,
          zone: cooldown.intensity,
          type: 'cooldown',
          label: 'Cooldown',
        });
        currentTime += cooldown.duration;
      }
    } else if (structure.type === 'pyramid' && structure.pyramid) {
      const { warmup, segments, cooldown } = structure.pyramid;

      if (warmup) {
        profile.push({
          start: currentTime,
          end: currentTime + warmup.duration,
          zone: warmup.intensity,
          type: 'warmup',
          label: 'Warmup',
        });
        currentTime += warmup.duration;
      }

      segments.forEach((segment, index) => {
        profile.push({
          start: currentTime,
          end: currentTime + segment.duration,
          zone: segment.intensity,
          type: 'segment',
          label: `Segment ${index + 1}`,
        });
        currentTime += segment.duration;
      });

      if (cooldown) {
        profile.push({
          start: currentTime,
          end: currentTime + cooldown.duration,
          zone: cooldown.intensity,
          type: 'cooldown',
          label: 'Cooldown',
        });
        currentTime += cooldown.duration;
      }
    } else if (structure.type === 'custom' && structure.custom) {
      structure.custom.segments.forEach((segment, index) => {
        profile.push({
          start: currentTime,
          end: currentTime + segment.duration,
          zone: segment.intensity,
          type: 'segment',
          label: segment.name || `Segment ${index + 1}`,
        });
        currentTime += segment.duration;
      });
    } else {
      const simple = structure.simple || { duration: workoutData.targetDuration || 3600, intensity: workoutData.targetIntensity?.zone || 2 };
      profile.push({
        start: 0,
        end: simple.duration,
        zone: simple.intensity,
        type: 'main',
        label: 'Main Effort',
      });
    }

    setIntensityProfile(profile);
  }

  function renderWorkoutSteps() {
    if (!workout || !intensityProfile.length) return null;

    const structure = workout.structure || { type: 'simple' };
    const steps = [];

    if (structure.type === 'intervals' && structure.intervals) {
      const { warmup, sets, cooldown } = structure.intervals;

      if (warmup) {
        steps.push({
          step: 1,
          name: 'Warmup',
          duration: warmup.duration,
          zone: warmup.intensity,
          description: `Easy warmup in Zone ${warmup.intensity} (${getZoneName(warmup.intensity)})`,
        });
      }

      sets.forEach((set, setIndex) => {
        steps.push({
          step: steps.length + 1,
          name: `Interval Set ${setIndex + 1}`,
          duration: set.workDuration * set.repetitions + (set.restDuration * (set.repetitions - 1)),
          zone: set.workIntensity,
          description: `${set.repetitions} × ${formatTime(set.workDuration)} @ Zone ${set.workIntensity} (${getZoneName(set.workIntensity)}), ${formatTime(set.restDuration)} rest`,
          repetitions: set.repetitions,
          workDuration: set.workDuration,
          restDuration: set.restDuration,
        });
      });

      if (cooldown) {
        steps.push({
          step: steps.length + 1,
          name: 'Cooldown',
          duration: cooldown.duration,
          zone: cooldown.intensity,
          description: `Easy cooldown in Zone ${cooldown.intensity} (${getZoneName(cooldown.intensity)})`,
        });
      }
    } else if (structure.type === 'pyramid' && structure.pyramid) {
      const { warmup, segments, cooldown } = structure.pyramid;

      if (warmup) {
        steps.push({
          step: 1,
          name: 'Warmup',
          duration: warmup.duration,
          zone: warmup.intensity,
          description: `Easy warmup in Zone ${warmup.intensity} (${getZoneName(warmup.intensity)})`,
        });
      }

      segments.forEach((segment, index) => {
        steps.push({
          step: steps.length + 1,
          name: `Segment ${index + 1}`,
          duration: segment.duration,
          zone: segment.intensity,
          description: `${formatDuration(segment.duration)} @ Zone ${segment.intensity} (${getZoneName(segment.intensity)})`,
        });
      });

      if (cooldown) {
        steps.push({
          step: steps.length + 1,
          name: 'Cooldown',
          duration: cooldown.duration,
          zone: cooldown.intensity,
          description: `Easy cooldown in Zone ${cooldown.intensity} (${getZoneName(cooldown.intensity)})`,
        });
      }
    } else if (structure.type === 'custom' && structure.custom) {
      structure.custom.segments.forEach((segment, index) => {
        steps.push({
          step: index + 1,
          name: segment.name || `Segment ${index + 1}`,
          duration: segment.duration,
          zone: segment.intensity,
          description: segment.description || `${formatDuration(segment.duration)} @ Zone ${segment.intensity} (${getZoneName(segment.intensity)})`,
        });
      });
    } else {
      const simple = structure.simple || { duration: workout.targetDuration || 3600, intensity: workout.targetIntensity?.zone || 2 };
      steps.push({
        step: 1,
        name: 'Main Effort',
        duration: simple.duration,
        zone: simple.intensity,
        description: `Steady effort in Zone ${simple.intensity} (${getZoneName(simple.intensity)})`,
      });
    }

    return steps;
  }

  function renderIntensityGraph(metricType, zoneData, powerZones, hrZones) {
    if (!intensityProfile || !intensityProfile.length) return null;

    const totalDuration = intensityProfile[intensityProfile.length - 1]?.end || workout?.targetDuration || 3600;
    const maxZone = Math.max(...intensityProfile.map(p => p.zone));

    function getZoneEstimate(zone) {
      if (!zoneData || !zoneData[`zone${zone}`]) return null;
      const zoneInfo = zoneData[`zone${zone}`];
      if (metricType === 'Power') {
        return `${zoneInfo.min}-${zoneInfo.max}W`;
      } else if (metricType === 'HR') {
        return `${zoneInfo.min}-${zoneInfo.max} bpm`;
      }
      return null;
    }
    function normZone(zone) {
      return Math.min(5, Math.max(1, Math.round(Number(zone))));
    }
    function getZoneEstimatePower(zone) {
      const z = normZone(zone);
      if (!powerZones?.[`zone${z}`]) return null;
      const r = powerZones[`zone${z}`];
      return `${r.min}–${r.max} W`;
    }
    function getZoneEstimateHR(zone) {
      const z = normZone(zone);
      if (!hrZones?.[`zone${z}`]) return null;
      const r = hrZones[`zone${z}`];
      return `${r.min}–${r.max} bpm`;
    }

    const handleBarHover = (segment, index, event) => {
      setHoveredSegment({ segment, index });
      const rect = event.currentTarget.getBoundingClientRect();
      const containerRect = event.currentTarget.closest('.workout-graph-container')?.getBoundingClientRect();
      if (containerRect) {
        setTooltipPosition({
          x: rect.left - containerRect.left + rect.width / 2,
          y: rect.top - containerRect.top - 10,
        });
      }
    };

    const handleBarLeave = () => {
      setHoveredSegment(null);
    };

    return (
      <div className="workout-graph-container" style={{ position: 'relative' }}>
        <div className="workout-graph-header">
          <FaChartLine /> Intensity Profile
        </div>
        <div className="workout-graph">
          <div className="workout-graph-bars" style={{ position: 'relative' }}>
            {intensityProfile.map((segment, index) => {
              const width = ((segment.end - segment.start) / totalDuration) * 100;
              const height = (segment.zone / maxZone) * 100;
              const zoneEstimate = getZoneEstimate(segment.zone);
              return (
                <div
                  key={index}
                  className="workout-graph-bar"
                  style={{
                    width: `${width}%`,
                    height: `${height}%`,
                    backgroundColor: getZoneColor(segment.zone),
                    minHeight: '20px',
                    position: 'relative',
                  }}
                  onMouseEnter={(e) => handleBarHover(segment, index, e)}
                  onMouseLeave={handleBarLeave}
                  onMouseMove={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const containerRect = e.currentTarget.closest('.workout-graph-container')?.getBoundingClientRect();
                    if (containerRect) {
                      setTooltipPosition({
                        x: rect.left - containerRect.left + rect.width / 2,
                        y: rect.top - containerRect.top - 10,
                      });
                    }
                  }}
                >
                  {height >= 30 && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        color: '#fff',
                        fontSize: '11px',
                        fontWeight: 700,
                        textShadow: '0 1px 2px rgba(0,0,0,0.8)',
                        textAlign: 'center',
                        pointerEvents: 'none',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <div>{getZoneName(segment.zone)}</div>
                      {(getZoneEstimatePower(segment.zone) || getZoneEstimateHR(segment.zone) || zoneEstimate) && (
                        <div style={{ fontSize: '9px', marginTop: '2px', opacity: 0.9 }}>
                          {getZoneEstimatePower(segment.zone) && <span>{getZoneEstimatePower(segment.zone)}</span>}
                          {getZoneEstimatePower(segment.zone) && getZoneEstimateHR(segment.zone) && ' · '}
                          {getZoneEstimateHR(segment.zone) && <span>{getZoneEstimateHR(segment.zone)}</span>}
                          {!getZoneEstimatePower(segment.zone) && !getZoneEstimateHR(segment.zone) && zoneEstimate && <span>{zoneEstimate}</span>}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="workout-graph-legend">
            <div className="workout-graph-time">0:00</div>
            <div className="workout-graph-time">{formatTime(totalDuration)}</div>
          </div>
        </div>
        {hoveredSegment && (
          <div
            className="workout-graph-tooltip"
            style={{
              position: 'absolute',
              left: `${tooltipPosition.x}px`,
              top: `${tooltipPosition.y}px`,
              transform: 'translate(-50%, -100%)',
              pointerEvents: 'none',
              zIndex: 1000,
            }}
          >
            <div className="workout-graph-tooltip-content">
              <div className="workout-graph-tooltip-label">{hoveredSegment.segment.label}</div>
              <div className="workout-graph-tooltip-duration">
                Duration: {formatDuration(hoveredSegment.segment.end - hoveredSegment.segment.start)}
              </div>
              <div className="workout-graph-tooltip-zone">
                Zone {hoveredSegment.segment.zone}: {getZoneName(hoveredSegment.segment.zone)}
              </div>
              {(getZoneEstimatePower(hoveredSegment.segment.zone) || getZoneEstimateHR(hoveredSegment.segment.zone) || getZoneEstimate(hoveredSegment.segment.zone)) && (
                <div className="workout-graph-tooltip-estimate">
                  <span className="workout-graph-tooltip-range-label">Range: </span>
                  {getZoneEstimatePower(hoveredSegment.segment.zone) && <span><FaBolt /> {getZoneEstimatePower(hoveredSegment.segment.zone)}</span>}
                  {getZoneEstimatePower(hoveredSegment.segment.zone) && getZoneEstimateHR(hoveredSegment.segment.zone) && ' · '}
                  {getZoneEstimateHR(hoveredSegment.segment.zone) && <span><FaHeartbeat /> {getZoneEstimateHR(hoveredSegment.segment.zone)}</span>}
                  {!getZoneEstimatePower(hoveredSegment.segment.zone) && !getZoneEstimateHR(hoveredSegment.segment.zone) && getZoneEstimate(hoveredSegment.segment.zone) && (
                    <span>{metricType}: {getZoneEstimate(hoveredSegment.segment.zone)}</span>
                  )}
                </div>
              )}
            </div>
            <div className="workout-graph-tooltip-arrow" />
          </div>
        )}
        <div className="workout-zone-legend">
          <div className="workout-zone-legend-title">Zone ranges (reminder)</div>
          {[1, 2, 3, 4, 5].map(zone => {
            const powerR = getZoneEstimatePower(zone);
            const hrR = getZoneEstimateHR(zone);
            const zoneEstimate = getZoneEstimate(zone);
            const hasRange = powerR || hrR || zoneEstimate;
            return (
              <div key={zone} className="workout-zone-item">
                <div
                  className="workout-zone-color"
                  style={{ backgroundColor: getZoneColor(zone) }}
                />
                <div className="workout-zone-legend-text">
                  <div className="workout-zone-legend-name">Zone {zone}: {getZoneName(zone)}</div>
                  {hasRange && (
                    <div className="workout-zone-legend-ranges">
                      {powerR && <span><FaBolt /> {powerR}</span>}
                      {powerR && hrR && ' · '}
                      {hrR && <span><FaHeartbeat /> {hrR}</span>}
                      {!powerR && !hrR && zoneEstimate && <span>{zoneEstimate}</span>}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  if (!workout) return null;

  const steps = renderWorkoutSteps();
  const scheduledDate = toDate(workout.scheduledDate);
  
  let metricType = 'Zone';
  let zoneData = null;
  
  if (workout.targetIntensity?.hrZone || (trainingZones?.hrZones && !trainingZones?.powerZones)) {
    metricType = 'HR';
    zoneData = trainingZones?.hrZones;
  } else if (workout.targetIntensity?.powerZone || trainingZones?.powerZones) {
    metricType = 'Power';
    zoneData = trainingZones?.powerZones;
  } else if (workout.targetIntensity?.paceZone || trainingZones?.paceZones) {
    metricType = 'Pace';
    zoneData = trainingZones?.paceZones;
  }
  
  function getZoneRange(zone) {
    if (!zoneData || !zoneData[`zone${zone}`]) return null;
    const zoneInfo = zoneData[`zone${zone}`];
    if (metricType === 'Pace') {
      const formatPace = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = Math.round(seconds % 60);
        return `${mins}:${String(secs).padStart(2, '0')}`;
      };
      return `${formatPace(zoneInfo.min)} - ${formatPace(zoneInfo.max)} /km`;
    }
    return `${zoneInfo.min} - ${zoneInfo.max}${metricType === 'HR' ? ' bpm' : ' W'}`;
  }

  const powerZonesData = trainingZones?.powerZones || null;
  const hrZonesData = trainingZones?.hrZones || null;
  function zoneKey(zone) {
    return Math.min(5, Math.max(1, Math.round(Number(zone))));
  }
  function getPowerRangeForZone(zone) {
    const z = zoneKey(zone);
    if (powerZonesData?.[`zone${z}`]) {
      const r = powerZonesData[`zone${z}`];
      return `${r.min}–${r.max} W`;
    }
    if (z === zoneKey(workout?.targetIntensity?.zone) && workout?.targetPowerRange) {
      return `${workout.targetPowerRange.min}–${workout.targetPowerRange.max} W`;
    }
    return null;
  }
  function getHRRangeForZone(zone) {
    const z = zoneKey(zone);
    if (hrZonesData?.[`zone${z}`]) {
      const r = hrZonesData[`zone${z}`];
      return `${r.min}–${r.max} bpm`;
    }
    if (z === zoneKey(workout?.targetIntensity?.zone) && workout?.targetHRRange) {
      return `${workout.targetHRRange.min}–${workout.targetHRRange.max} bpm`;
    }
    return null;
  }

  const zonesUsed = intensityProfile.length
    ? [...new Set(intensityProfile.map((p) => Math.round(p.zone)))].sort((a, b) => a - b)
    : [];
  const primaryZone = workout.targetIntensity?.zone || workout.structure?.simple?.intensity || 2;

  function safeFilename(name) {
    return (name || 'workout').replace(/[^\w\s-]/g, '').replace(/\s+/g, '-').slice(0, 60) || 'workout';
  }

  async function handleExportTcx() {
    setExportingTcx(true);
    try {
      const xml = buildTcxWorkout(workout);
      downloadWorkoutFile(xml, `${safeFilename(workout.name)}.tcx`);
    } finally {
      setExportingTcx(false);
    }
  }

  async function handleExportFit() {
    setExportingFit(true);
    try {
      const bytes = await buildFitWorkout(workout, { trainingZones });
      if (bytes) {
        downloadWorkoutFile(bytes, `${safeFilename(workout.name)}.fit`);
      } else {
        alert('FIT export is not available.');
      }
    } finally {
      setExportingFit(false);
    }
  }

  return (
    <div className="workout-detail-inline-content">
      <div className="workout-detail-export-row">
        <button type="button" className="workout-inline-export-btn" onClick={handleExportTcx} disabled={exportingTcx} title="Export as TCX">
          <FaFileDownload /> {exportingTcx ? '…' : 'TCX'}
        </button>
        <button type="button" className="workout-inline-export-btn" onClick={handleExportFit} disabled={exportingFit} title="Export as FIT">
          <FaFileDownload /> {exportingFit ? '…' : 'FIT'}
        </button>
      </div>
      <div className="workout-detail-info">
        {workout.description && (
          <div className="workout-detail-description">
            <h3>Description</h3>
            <p>{workout.description}</p>
          </div>
        )}

        {renderIntensityGraph(metricType, zoneData, powerZonesData, hrZonesData)}

        <div className="workout-detail-steps">
          <h3>Workout Structure</h3>
          <div className="workout-steps-list">
            {steps && steps.length > 0 ? steps.map((step, index) => (
              <div key={index} className="workout-step-card">
                <div className="workout-step-header">
                  <div className="workout-step-number">Step {step.step}</div>
                  <div
                    className="workout-step-zone"
                    style={{ backgroundColor: getZoneColor(step.zone) }}
                  >
                    {metricType} Zone {step.zone}
                  </div>
                </div>
                <div className="workout-step-name">{step.name}</div>
                <div className="workout-step-details">
                  <div className="workout-step-duration">
                    <FaClock /> {formatDuration(step.duration)}
                  </div>
                  <div className="workout-step-description">{step.description}</div>
                  {(getPowerRangeForZone(step.zone) || getHRRangeForZone(step.zone) || getZoneRange(step.zone)) && (
                    <div className="workout-step-zone-range">
                      {getPowerRangeForZone(step.zone) && <span><FaBolt /> {getPowerRangeForZone(step.zone)}</span>}
                      {getPowerRangeForZone(step.zone) && getHRRangeForZone(step.zone) && ' · '}
                      {getHRRangeForZone(step.zone) && <span><FaHeartbeat /> {getHRRangeForZone(step.zone)}</span>}
                      {!getPowerRangeForZone(step.zone) && !getHRRangeForZone(step.zone) && getZoneRange(step.zone) && (
                        <span>{metricType === 'HR' && <FaHeartbeat />}{metricType === 'Power' && <FaBolt />} {getZoneRange(step.zone)}</span>
                      )}
                    </div>
                  )}
                </div>
                {step.repetitions && (
                  <div className="workout-step-intervals">
                    <div className="workout-interval-detail">
                      <strong>Work:</strong> {formatTime(step.workDuration)} @ Zone {step.zone}
                    </div>
                    <div className="workout-interval-detail">
                      <strong>Rest:</strong> {formatTime(step.restDuration)} @ Zone 1
                    </div>
                    <div className="workout-interval-detail">
                      <strong>Repetitions:</strong> {step.repetitions}
                    </div>
                  </div>
                )}
              </div>
            )) : (
              <div className="workout-steps-empty">No workout structure available</div>
            )}
          </div>
        </div>

        <div className="workout-detail-summary">
          <h3>Summary</h3>
          <div className="workout-summary-grid">
            <div className="workout-summary-item">
              <div className="workout-summary-label">Total Duration</div>
              <div className="workout-summary-value">{formatDuration(workout.targetDuration)}</div>
            </div>
            {workout.targetTSS && (
              <div className="workout-summary-item">
                <div className="workout-summary-label">Target TSS</div>
                <div className="workout-summary-value">{workout.targetTSS}</div>
              </div>
            )}
            <div className="workout-summary-item">
              <div className="workout-summary-label">Primary Zone</div>
              <div className="workout-summary-value">
                {metricType} Zone {workout.targetIntensity?.zone || workout.structure?.simple?.intensity || 2}
                {(getPowerRangeForZone(primaryZone) || getHRRangeForZone(primaryZone) || getZoneRange(primaryZone)) && (
                  <div className="workout-summary-zone-range">
                    {getPowerRangeForZone(primaryZone) && <span><FaBolt /> {getPowerRangeForZone(primaryZone)}</span>}
                    {getPowerRangeForZone(primaryZone) && getHRRangeForZone(primaryZone) && ' · '}
                    {getHRRangeForZone(primaryZone) && <span><FaHeartbeat /> {getHRRangeForZone(primaryZone)}</span>}
                    {!getPowerRangeForZone(primaryZone) && !getHRRangeForZone(primaryZone) && getZoneRange(primaryZone) && (
                      <span>{getZoneRange(primaryZone)}</span>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="workout-summary-item">
              <div className="workout-summary-label">Type</div>
              <div className="workout-summary-value">{workout.type || 'endurance'}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
